import React, { useState, useEffect } from 'react';
import { fetchAllData } from '../../helpers/externapi';
import * as XLSX from 'xlsx';

const DashboardLeaderBoardMonthlyAdvisors = () => {
    const [leaderData, setLeaderData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [visibleCount, setVisibleCount] = useState(10);

    const PAGE_SIZE = 10;

    const today = new Date();
    const currentMonthIndex = today.getMonth();

    const allMonths = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    // Show only months up to current month, then reverse to show current month first
    const months = allMonths.slice(0, currentMonthIndex + 1).reverse();

    // Fixed cols:
    // Sl No + Name + HRM + HAO + DOJ
    const totalCols = 5 + months.length;

    useEffect(() => {
        const fetchLeaderBoard = async () => {
            try {
                setLoading(true);

                const data = await fetchAllData(
                    'lambdaAPI/Employee/GetLeaderBoardMonthlyAdvisors'
                );

                if (data && data.status && data.data) {
                    setLeaderData(data.data);
                } else if (Array.isArray(data)) {
                    setLeaderData(data);
                }
            } catch (error) {
                console.error(
                    'Error fetching leaderboard Advisors data:',
                    error
                );
            } finally {
                setLoading(false);
            }
        };

        fetchLeaderBoard();
    }, []);

    // ───────────────────────────────────────────────
    // Get HRM & HAO Names
    // ───────────────────────────────────────────────
    const getHRMName = (row) => {
        return row.ReportingToRole === 'Health Relationship Manager'
            ? row.ReportingToName || '-'
            : '-';
    };

    const getHAOName = (row) => {
        return row.ReportingToRole === 'Health Account Officer'
            ? row.ReportingToName || '-'
            : '-';
    };

    // ───────────────────────────────────────────────
    // Excel Download
    // ───────────────────────────────────────────────
    const handleDownloadExcel = () => {
        const excelData = leaderData.map((row, index) => {
            const base = {
                'Sl No': row['Sl No'] ?? index + 1,
                Name: row.Name || '',
                HRM: getHRMName(row),
                HAO: getHAOName(row),
                'Date of Joining': row['Date of Joining'] || ''
            };

            months.forEach((month) => {
                base[month] = row[month] ?? 0;
            });

            return base;
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);

        const cols = [
            { wch: 8 },   // Sl No
            { wch: 28 },  // Name
            { wch: 28 },  // HRM
            { wch: 28 },  // HAO
            { wch: 18 }   // DOJ
        ];

        months.forEach(() => cols.push({ wch: 10 }));

        worksheet['!cols'] = cols;

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            'Monthly Advisors'
        );

        const fileName = `LeaderBoardMonthlyAdvisors_${today
            .toISOString()
            .slice(0, 10)}.xlsx`;

        XLSX.writeFile(workbook, fileName);
    };

    const skeletonStyles = `
        .lbma-shimmer {
            animation-duration: 2.5s;
            animation-fill-mode: forwards;
            animation-iteration-count: infinite;
            animation-name: lbmaShimmer;
            animation-timing-function: linear;
            background: linear-gradient(
                to right,
                #e0e0e0 8%,
                #f0f0f0 18%,
                #e0e0e0 33%
            );
            background-size: 1000px 104px;
            border-radius: 4px;
        }

        @keyframes lbmaShimmer {
            0% {
                background-position: -1000px 0;
            }

            100% {
                background-position: 1000px 0;
            }
        }

        .lbma-thead-main th {
            background-color: #f8f9fa;
            font-size: 12px;
            font-weight: 700;
            color: #555;
            border-bottom: 2px solid #dee2e6;
            padding: 8px 6px;
        }

        .lbma-cell-center {
            text-align: center;
        }

        .lbma-cell-value {
            font-weight: 600;
            color: #333;
        }

        .lbma-cell-zero {
            color: #bbb;
        }

        .lbma-show-more-row td {
            background-color: #f8f9fa;
            text-align: center;
            padding: 10px;
            border-top: 2px solid #dee2e6;
        }

        .lbma-show-more-btn {
            background: none;
            border: none;
            color: #4287f5;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
            padding: 0;
        }

        .lbma-show-more-btn:hover {
            text-decoration: underline;
        }

        .lbma-show-less-btn {
            background: none;
            border: none;
            color: #888;
            font-size: 13px;
            font-weight: 600;
            cursor: pointer;
            padding: 0;
            margin-left: 16px;
        }

        .lbma-show-less-btn:hover {
            text-decoration: underline;
            color: #555;
        }

        .lbma-count-badge {
            font-size: 12px;
            color: #999;
            margin-left: 8px;
        }
    `;

    const visibleData = leaderData.slice(0, visibleCount);

    const totalCount = leaderData.length;

    const hasMore = visibleCount < totalCount;

    const hasLess = visibleCount > PAGE_SIZE;

    const skeletonRows = () =>
        [...Array(PAGE_SIZE)].map((_, i) => (
            <tr key={i}>
                <td>
                    <div
                        className="lbma-shimmer"
                        style={{ width: 20, height: 14 }}
                    ></div>
                </td>

                <td>
                    <div
                        className="lbma-shimmer"
                        style={{ width: 140, height: 14 }}
                    ></div>
                </td>

                <td>
                    <div
                        className="lbma-shimmer"
                        style={{ width: 140, height: 14 }}
                    ></div>
                </td>

                <td>
                    <div
                        className="lbma-shimmer"
                        style={{ width: 140, height: 14 }}
                    ></div>
                </td>

                <td>
                    <div
                        className="lbma-shimmer"
                        style={{ width: 90, height: 14 }}
                    ></div>
                </td>

                {months.map((_, j) => (
                    <td key={j} className="lbma-cell-center">
                        <div
                            className="lbma-shimmer"
                            style={{
                                width: 28,
                                height: 14,
                                margin: '0 auto'
                            }}
                        ></div>
                    </td>
                ))}
            </tr>
        ));

    const renderValue = (val) => {
        if (val === null || val === undefined || val === 0) {
            return <span className="lbma-cell-zero">0</span>;
        }

        return <span className="lbma-cell-value">{val}</span>;
    };

    return (
        <div className="card mt-4" style={{ zIndex: 1 }}>
            <style>{skeletonStyles}</style>

            <div className="card-header align-items-center d-flex justify-content-between">
                <h5 className="card-action-title mb-0">
                    Leader Board Monthly - Advisors
                </h5>

                <button
                    className="btn btn-sm btn-outline-success"
                    onClick={handleDownloadExcel}
                    disabled={loading || leaderData.length === 0}
                    title="Download as Excel"
                >
                    <i className="fa-solid fa-file-excel me-1"></i>
                    Download Excel
                </button>
            </div>

            <div className="card-body p-0">
                <div className="table-responsive">
                    <table
                        className="table table-bordered mb-0"
                        style={{ fontSize: 13 }}
                    >
                        <thead>
                            <tr className="lbma-thead-main">
                                <th className="align-middle">Sl No</th>

                                <th className="align-middle">Name</th>

                                <th className="align-middle">HRM</th>

                                <th className="align-middle">HAO</th>

                                <th className="align-middle">
                                    Date of Joining
                                </th>

                                {months.map((month) => (
                                    <th
                                        key={month}
                                        className="lbma-cell-center align-middle"
                                    >
                                        {month}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {loading ? (
                                skeletonRows()
                            ) : leaderData.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={totalCols}
                                        className="text-center py-4 text-muted"
                                    >
                                        No leaderboard data available
                                    </td>
                                </tr>
                            ) : (
                                <>
                                    {visibleData.map((row, index) => (
                                        <tr key={row.EmployeeId ?? index}>
                                            <td>
                                                {row['Sl No'] ?? index + 1}
                                            </td>

                                            <td
                                                style={{
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {row.Name || '-'}
                                            </td>

                                            <td
                                                style={{
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {getHRMName(row)}
                                            </td>

                                            <td
                                                style={{
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {getHAOName(row)}
                                            </td>

                                            <td
                                                className="lbma-cell-center"
                                                style={{
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {row['Date of Joining'] || '-'}
                                            </td>

                                            {months.map((month) => (
                                                <td
                                                    key={month}
                                                    className="lbma-cell-center"
                                                >
                                                    {renderValue(row[month])}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}

                                    {(hasMore || hasLess) && (
                                        <tr className="lbma-show-more-row">
                                            <td colSpan={totalCols}>
                                                {hasMore && (
                                                    <button
                                                        className="lbma-show-more-btn"
                                                        onClick={() =>
                                                            setVisibleCount(
                                                                (prev) =>
                                                                    prev +
                                                                    PAGE_SIZE
                                                            )
                                                        }
                                                    >
                                                        <i
                                                            className="fa-solid fa-chevron-down me-1"
                                                            style={{
                                                                fontSize: 11
                                                            }}
                                                        ></i>

                                                        Show more

                                                        <span className="lbma-count-badge">
                                                            ({visibleCount} of{' '}
                                                            {totalCount} shown)
                                                        </span>
                                                    </button>
                                                )}

                                                {hasLess && (
                                                    <button
                                                        className="lbma-show-less-btn"
                                                        onClick={() =>
                                                            setVisibleCount(
                                                                PAGE_SIZE
                                                            )
                                                        }
                                                    >
                                                        <i
                                                            className="fa-solid fa-chevron-up me-1"
                                                            style={{
                                                                fontSize: 11
                                                            }}
                                                        ></i>

                                                        Show less
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="card-footer py-2">
                <small className="text-muted">
                    {!loading && totalCount > 0 && (
                        <span>
                            Total: <strong>{totalCount}</strong> records
                        </span>
                    )}
                </small>
            </div>
        </div>
    );
};

export default DashboardLeaderBoardMonthlyAdvisors;